import { useEffect, useState } from 'react';
import cover from '../assets/thumb-duolingo.jpg';
import pdfIcon from './assets/pdf.svg';
import presentIcon from './assets/present.svg';
import shareIcon from './assets/share.svg';
import { Stage, after } from './Stage';
import styles from './share.module.css';

/** The cover lands, people join (1), the menu opens (2) on Present (3), and the people reading it stack up (4). */
const MARKS = [450, 950, 1400, 1750];

type Action = 'present' | 'share' | 'pdf';
const ACTIONS: { id: Action; label: string; done?: string; icon: string }[] = [
  { id: 'present', label: 'Present', icon: presentIcon },
  { id: 'share', label: 'Share link', done: 'Link copied', icon: shareIcon },
  { id: 'pdf', label: 'Download PDF', done: 'PDF saved', icon: pdfIcon },
];

const READERS = [
  { initials: 'JD', tone: styles.blue },
  { initials: 'AM', tone: styles.pink },
  { initials: 'RK', tone: styles.tan },
];

export function ShareScene({ label }: { label: string }) {
  const [hovered, setHovered] = useState<number | null>(null);
  const [done, setDone] = useState<Action | null>(null);
  const [presenting, setPresenting] = useState(0);

  // A finished action says so for a moment, then the menu goes back to how it was.
  useEffect(() => {
    if (!done) return;
    const timer = window.setTimeout(() => setDone(null), 1600);
    return () => window.clearTimeout(timer);
  }, [done]);

  const act = (id: Action) => (id === 'present' ? setPresenting((n) => n + 1) : setDone(id));

  return (
    <Stage label={label} marks={MARKS} className={styles.scene} onPointerLeave={() => setHovered(null)}>
      {(step) => {
        const lit = hovered ?? (step >= 3 ? 0 : null);
        return (
          <>
            <div key={presenting} className={`${styles.cover} ${presenting ? styles.presenting : ''}`}>
              {/* The same Duolingo cover as the hero's middle card; its title is part of the picture. */}
              <span className={styles.coverArt} style={{ backgroundImage: `url(${cover})` }} />
            </div>

            <div className={`${styles.people} ${step >= 1 ? styles.in : ''}`}>
              {READERS.slice(0, 2).map((reader, i) => (
                <span key={reader.initials} className={styles.tile} style={after(i * 140)}>
                  <span className={`${styles.face} ${reader.tone} ${i === 0 ? styles.talking : ''}`}>{reader.initials}</span>
                </span>
              ))}
            </div>

            <div className={`${styles.menu} ${step >= 2 ? styles.in : ''}`}>
              <span className={`${styles.highlight} ${lit === null ? '' : styles.on}`} style={{ translate: `0 ${(lit ?? 0) * 36}px` }} />
              {ACTIONS.map((action, i) => (
                <button
                  key={action.id}
                  type="button"
                  className={styles.item}
                  style={after(80 + i * 60)}
                  onPointerEnter={() => setHovered(i)}
                  onFocus={() => setHovered(i)}
                  onClick={() => act(action.id)}
                >
                  {done === action.id ? (
                    <svg className={styles.icon} viewBox="0 0 24 24" aria-hidden>
                      <path
                        d="M5 12.5l4.5 4.5L19 7.5"
                        fill="none"
                        stroke="#2C6D3E"
                        strokeWidth="2.2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  ) : (
                    <img className={styles.icon} src={action.icon} alt="" />
                  )}
                  <span key={done === action.id ? 'done' : 'label'} className={styles.itemLabel}>
                    {done === action.id ? action.done : action.label}
                  </span>
                </button>
              ))}
            </div>

            <div className={`${styles.readers} ${step >= 4 ? styles.in : ''}`}>
              <span className={styles.faces}>
                {READERS.map((reader, i) => (
                  <span key={reader.initials} className={`${styles.face} ${styles.small} ${reader.tone}`} style={after(200 + i * 110)}>
                    {reader.initials}
                  </span>
                ))}
              </span>
              <span className={styles.readersBar} />
            </div>
          </>
        );
      }}
    </Stage>
  );
}
